import { ColumnDef } from "@tanstack/react-table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { formatPhoneNumber } from "@/lib/utils";
import { Mail, Clock, Play, Phone, ExternalLink, CalendarClock } from "lucide-react";
import { Link } from "react-router-dom";
import { format, formatDistanceToNow, isAfter, isBefore, startOfDay } from "date-fns";
import type { Contact } from "@/types/contact";
import { useState } from "react";
import { SkipContactDialog } from "./SkipContactDialog";
import { useToast } from '@/hooks/use-toast';
import { cn } from "@/lib/utils";
import { useEmailComposer } from "@/components/email/EmailComposerProvider";
import { CreateActivityDialog } from "./details/CreateActivityDialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const getLastContactedInfo = (contact: Contact) => {
  if (!contact.last_contacted) return '';
  
  const date = new Date(contact.last_contacted);
  const relativeTime = formatDistanceToNow(date, { addSuffix: true });
  const type = contact.last_contact_type ? ` via ${contact.last_contact_type.replace('_', ' ')}` : '';
  
  return `${relativeTime}${type}`;
};

// Renders a contact's oldest outstanding scheduled activity (date + time) for the "Event"
// line. Same red-past / blue-future / today color logic as the Next-contact line.
const getEventInfo = (startDate: string) => {
  const date = new Date(startDate);
  const today = startOfDay(new Date());
  const formattedDate = format(date, 'MM/dd/yyyy h:mm a');

  const className = cn(
    "font-medium",
    isAfter(startOfDay(date), today) ? "text-blue-600 dark:text-blue-400" :
    isBefore(startOfDay(date), today) ? "text-red-600 dark:text-red-400" :
    "text-foreground"
  );

  return <span className={className}>{formattedDate}</span>;
};

const MAX_COMPANY_NAME_LENGTH = 30;

const truncateText = (text: string | null | undefined, maxLength: number): string => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

const getNextContactInfo = (contact: Contact) => {
  if (!contact.next_contact) return 'Not scheduled';
  
  const date = new Date(contact.next_contact);
  const today = startOfDay(new Date());
  const formattedDate = format(date, 'MM/dd/yyyy');
  
  const className = cn(
    "font-medium",
    isAfter((startOfDay(date)), today) ? "text-blue-600 dark:text-blue-400" : // Future date
    isBefore((startOfDay(date)), today) ? "text-red-600 dark:text-red-400" : // Past date
    "text-foreground" // Today
  );
  
  return <span className={className}>{formattedDate}</span>;
};

export const columns: ColumnDef<Contact>[] = [
  {
    id: "select",
    size: 50,
    minSize: 50,
    header: ({ table }) => (
      <div className="px-2">
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      </div>
    ),
    cell: ({ row }) => (
      <div className="px-2">
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      </div>
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: "name",
    size: 280,
    header: "Name",
    cell: ({ row, table }) => {
      const contact = row.original;
      const oldestEventStart = table.options.meta?.activitiesByContactId?.[contact.id];
      const displayName = contact.first_name || contact.last_name
        ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim()
        : contact.company_name || contact.primary_email || 'Unnamed Contact';
      const isCompanyAsPrimary = !contact.first_name && !contact.last_name && contact.company_name;
      const needsTruncation = isCompanyAsPrimary && contact.company_name && contact.company_name.length > MAX_COMPANY_NAME_LENGTH;
      const companyNeedsTruncation = contact.company_name && contact.company_name.length > MAX_COMPANY_NAME_LENGTH;

      return (
        <div className="space-y-1 max-w-[250px]">
          {needsTruncation ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    to={`/contacts/${contact.id}`}
                    className="font-medium hover:underline block truncate"
                  >
                    {truncateText(displayName, MAX_COMPANY_NAME_LENGTH)}
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[300px]">
                  <p className="break-words">{contact.company_name}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <Link
              to={`/contacts/${contact.id}`}
              className="font-medium hover:underline block"
            >
              {displayName}
            </Link>
          )}
          {contact.company_name && (contact.first_name || contact.last_name) && (
            companyNeedsTruncation ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="text-sm text-muted-foreground truncate cursor-default">
                      {truncateText(contact.company_name, MAX_COMPANY_NAME_LENGTH)}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[300px]">
                    <p className="break-words">{contact.company_name}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <div className="text-sm text-muted-foreground">
                {contact.company_name}
              </div>
            )
          )}
          <div className="text-xs flex flex-col">
            <div className="flex items-center gap-1">
              <CalendarClock className="h-3 w-3 text-muted-foreground" />
              <span>Next contact: {getNextContactInfo(contact)}</span>
            </div>
            {oldestEventStart && (
              <div className="flex items-center gap-1 mt-0.5">
                <Clock className="h-3 w-3 text-muted-foreground" />
                <span>Event: {getEventInfo(oldestEventStart)}</span>
              </div>
            )}
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "phone",
    size: 140,
    header: "Phone",
    cell: ({ row }) => {
      const contact = row.original;
      const phone = contact.primary_phone;

      if (!phone) return "";

      return (
        <div className="flex items-center gap-2">
          <Link
            to={`/contacts/${contact.id}?call=true&phone=${encodeURIComponent(phone)}`}
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-1 text-primary hover:underline"
          >
            <Phone className="h-3 w-3" />
            {formatPhoneNumber(phone)}
          </Link>
        </div>
      );
    },
  },
  {
    id: "tools",
    size: 160,
    header: () => (
      <div className="text-center">Actions</div>
    ),
    cell: ({ row, table }) => {
      const contact = row.original;
      const [isSkipping] = useState(false);
      const [showSkipDialog, setShowSkipDialog] = useState(false);
      const [showActivityDialog, setShowActivityDialog] = useState(false);
      useToast();
      const { openComposer } = useEmailComposer();

      return (
        <>
          <div className="flex items-center justify-center gap-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openComposer({ to: contact.primary_email || '', contactId: contact.id })}
                    disabled={!contact.primary_email}
                    className="h-8 w-8 transition-colors"
                  >
                    <Mail className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>{contact.primary_email ? "Send email" : "No email address"}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => setShowActivityDialog(true)}
                    className="h-8 w-8 transition-colors"
                  >
                    <Clock className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>Create activity</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => setShowSkipDialog(true)}
                    disabled={isSkipping}
                    className="h-8 w-8 transition-colors"
                  >
                    <Play className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>Schedule next contact</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className="h-8 w-8 transition-colors"
                    onClick={() => {
                      window.open(`/contacts/${contact.id}`, '_blank');
                    }}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p>Open in new tab</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {showActivityDialog && (
            <CreateActivityDialog
              open={showActivityDialog}
              onOpenChange={setShowActivityDialog}
              contactId={contact.id}
              onSuccess={() => {
                table.options.meta?.onRowsChange?.();
              }}
            />
          )}
          <SkipContactDialog
            open={showSkipDialog}
            onOpenChange={setShowSkipDialog}
            contactId={contact.id}
            onSuccess={() => {
              table.options.meta?.onRowsChange?.();
            }}
          />
        </>
      );
    },
  },
  {
    accessorKey: "relationship",
    size: 100,
    header: "Relationship",
    cell: ({ row }) => {
      const relationship = row.getValue("relationship") as string;
      if (!relationship) return null;
      
      return (
        <Badge variant="outline" className="capitalize">
          {relationship}
        </Badge>
      );
    },
  },
  {
    accessorKey: "type",
    size: 90,
    header: "Type",
    cell: ({ row }) => {
      const type = row.getValue("type") as string;
      if (!type) return null;
      
      return (
        <Badge variant="outline" className="capitalize">
          {type}
        </Badge>
      );
    },
  },
  {
    accessorKey: "rank",
    size: 80,
    header: "Rank",
    cell: ({ row }) => {
      const rank = row.getValue("rank") as string;
      if (!rank) return null;
      
      return (
        <Badge variant={
          rank.toLowerCase() === 'hot' ? "destructive" :
          rank.toLowerCase() === 'warm' ? "default" : 
          "secondary"
        } className="capitalize">
          {rank}
        </Badge>
      );
    },
  },
  {
    accessorKey: "status",
    size: 90,
    header: "Status",
    cell: ({ row }) => {
      const status = row.getValue("status") as string;
      if (!status) return null;
      
      return (
        <Badge variant="outline" className="capitalize">
          {status}
        </Badge>
      );
    },
  },
  {
    accessorKey: "last_contacted",
    size: 130,
    header: "Last Contacted",
    cell: ({ row }) => {
      const contact = row.original;
      const lastContactedInfo = getLastContactedInfo(contact);

      if (!lastContactedInfo) return <span className="text-muted-foreground text-sm">Never</span>;

      return (
        <span className="text-sm">{lastContactedInfo}</span>
      );
    },
  },
  {
    accessorKey: "remarket",
    size: 100,
    header: "Remarket",
    cell: ({ row }) => {
      // Display normalization: prod has 6 raw patterns (YES/NO/true/false/Yes/NULL)
      // because the column accumulated mixed-case writes from multiple paths
      // (CSV imports, detail-page Select, unsubscribe flow). Display as
      // canonical "Yes" / "No" labels for readability. Unknown values fall
      // back to raw display (defensive — surfaces unexpected values for
      // cleanup rather than hiding them). NULL/empty → blank cell.
      const raw = row.original.remarket;
      if (!raw) return null;
      const normalized = String(raw).trim().toLowerCase();
      if (normalized === 'yes' || normalized === 'true') {
        return <span className="text-sm">Yes</span>;
      }
      if (normalized === 'no' || normalized === 'false') {
        return <span className="text-sm">No</span>;
      }
      if (normalized === 'unsubscribe' || normalized === 'unsubscribed') {
        return <span className="text-sm">Unsubscribed</span>;
      }
      return <span className="text-sm">{raw}</span>;
    },
  },
];